import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { useForm } from 'react-hook-form';
import GameCard from '../../components/GameCard';
import { BRANDING } from '../../config/branding';
import { SOCIAL_LINKS } from '../../utils/constants';
import MergedExampleGameData from '../../utils/mock_data/mergedExampleData';
import { auth } from '../../services/Firebase';
import { createCheckoutSession, createPublicCheckoutSession, getPackages } from '../../api/payment';
import useCheckoutConversion from '../../utils/hooks/useCheckoutConversion';
import {
  CHECKOUT_FEATURES,
  PACKAGE_FEATURES,
  PREVIEW_SPORT,
  PROPS_FEATURES,
} from './constants';
import {
  ActionBtn,
  CheckoutCard,
  CheckoutStack,
  DocsCtaLink,
  DocsCtaWrap,
  EmailGroup,
  EmailLabel,
  ErrorText,
  FeatRow,
  HowFeat,
  HowFeatures,
  HowGrid,
  HowIcon,
  HowNum,
  HowStep,
  Input,
  Notice,
  PkgH1,
  PkgHdr,
  PkgSub,
  PasswordHint,
  PreviewGrid,
  PreviewInner,
  RightBox,
  Sec,
  SecDesc,
  SecH2,
  SecInner,
  SelectedPackageLabel,
  SelectedPackageMeta,
  SelectedPackageName,
  SelectedPackageWrap,
  SkeletonLine,
  SportCard,
  SportCardGrid,
  SysDocCard,
  SysDocGrid,
  TopLine,
  TwoCol,
} from './styles';

const Packages = () => {
  const location = useLocation();
  const history = useHistory();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  useCheckoutConversion(location.search);

  const [selectedPlan, setSelectedPlan] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  const [packageData, setPackageData] = useState(null);
  const [packageLoading, setPackageLoading] = useState(true);
  const [packageError, setPackageError] = useState('');
  const [includePropsAddon, setIncludePropsAddon] = useState(false);
  const checkoutFormRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const loadPackageData = async () => {
      try {
        const data = await getPackages();
        const plans = Array.isArray(data?.plans) ? data.plans : [];
        const missingPlans = plans.filter(item => !item?.key || !item?.monthlyPrice);

        if (missingPlans.length) {
          throw new Error('Failed to load live Stripe package pricing. Please refresh and try again.');
        }

        if (!mounted) return;
        setPackageData(data);
      } catch (err) {
        if (!mounted) return;
        setPackageError(err.message || 'Failed to load live Stripe package pricing.');
      } finally {
        if (mounted) {
          setPackageLoading(false);
        }
      }
    };

    loadPackageData();

    return () => {
      mounted = false;
    };
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      checkoutEmail: '',
      checkoutName: '',
      checkoutPassword: ''
    }
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);

      if (user?.email) {
        setValue('checkoutEmail', user.email, { shouldDirty: false });
      }

      if (user?.displayName) {
        setValue('checkoutName', user.displayName, { shouldDirty: false });
      }
    });

    return unsubscribe;
  }, [setValue]);

  useEffect(() => {
    const emailFromQuery = query.get('email');
    if (emailFromQuery) {
      setValue('checkoutEmail', emailFromQuery, { shouldDirty: false });
    }
  }, [query, setValue]);

  const planPackages = useMemo(() => {
    const livePlans = Array.isArray(packageData?.plans) ? packageData.plans : [];

    return livePlans.map((plan, index) => ({
      id: plan.key,
      key: plan.key,
      label: plan.label,
      monthlyCredits: Number(plan.monthlyCredits || 0),
      monthlyPrice: plan.monthlyPrice || '',
      loading: packageLoading,
      unavailable: !packageLoading && Boolean(packageError),
      selectedByDefault: index === 0,
    }));
  }, [packageData, packageError, packageLoading]);

  useEffect(() => {
    if (selectedPlan) return;
    const firstPlanId = planPackages.find(item => item?.id)?.id || '';
    if (firstPlanId) {
      setSelectedPlan(firstPlanId);
    }
  }, [planPackages, selectedPlan]);

  const selected = useMemo(
    () => planPackages.find(item => item.id === selectedPlan) || planPackages.find(item => item.selectedByDefault),
    [selectedPlan, planPackages]
  );
  const propsAddon = useMemo(() => packageData?.propsAddon || null, [packageData]);
  const previewGame = MergedExampleGameData[PREVIEW_SPORT];

  const checkoutState = query.get('checkout');

  const selectPlanAndGuideCheckout = planId => {
    if (packageLoading) return;

    setSelectedPlan(planId);

    // On stacked/mobile layout, auto-scroll to the checkout form after package selection.
    if (window.matchMedia('(max-width: 980px)').matches) {
      window.requestAnimationFrame(() => {
        checkoutFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  const startCheckout = async values => {
    setError('');

    if (packageLoading) {
      setError('Still loading live Stripe pricing. Please wait a moment and try again.');
      return;
    }

    if (packageError) {
      setError(packageError);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        purchaseType: 'plan',
        planKey: selectedPlan,
        includePropsAddon,
        returnPath: '/login'
      };

      const session = auth.currentUser || currentUser
        ? await createCheckoutSession(payload)
        : await createPublicCheckoutSession({
            ...payload,
            email: String(values.checkoutEmail || '').trim(),
            displayName: String(values.checkoutName || '').trim(),
            password: String(values.checkoutPassword || '')
          });

      if (session?.url) {
        window.location.assign(session.url);
        return;
      }

      setError('Checkout session did not return a URL.');
    } catch (err) {
      setError(err.message || 'Failed to start checkout.');
    } finally {
      setSubmitting(false);
    }
  };

  const startPropsAddonCheckout = async () => {
    if (!currentUser) {
      setError('Sign in first to purchase the Props add-on.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const session = await createCheckoutSession({
        purchaseType: 'props_addon',
        returnPath: '/packages'
      });

      if (session?.url) {
        window.location.assign(session.url);
        return;
      }

      setError('Checkout session did not return a URL.');
    } catch (err) {
      setError(err.message || 'Failed to start add-on checkout.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Packages | {BRANDING.appName}</title>
        <meta name="description" content="Credit-based monthly plans with unlockable game prediction details." />
      </Helmet>

      {checkoutState === 'success' && (
        <Notice>
          Checkout completed successfully. Your access has been provisioned. Log in to continue.
        </Notice>
      )}

      {checkoutState === 'cancel' && <Notice $error>Checkout canceled. You can restart when ready.</Notice>}
      {packageError && <Notice $error>{packageError}</Notice>}

      <Sec bg="#f5f7fb">
        <SecInner>
          <PkgHdr>
            <PkgH1>Pick Your Winning Edge</PkgH1>
            <PkgSub>
              Launch checkout for a monthly credit plan. All sports are included, and credits are used to unlock
              prediction details game-by-game. New subscribers get 3 days for $1 - your monthly plan price starts
              after the trial.
            </PkgSub>
          </PkgHdr>

          <TwoCol>
            <div>
              <TopLine>Step 1</TopLine>
              <SecH2>Select Your Credit Plan</SecH2>
              <SecDesc>Plans refill monthly credits. Unlocking prediction details costs 1 credit, or 3 credits when the PW Rating is 70% or higher.</SecDesc>
              <SportCardGrid>
                {planPackages.map(pkg => (
                  <SportCard
                    key={pkg.id}
                    $sel={selectedPlan === pkg.id}
                    onClick={() => selectPlanAndGuideCheckout(pkg.id)}
                    disabled={packageLoading}
                  >
                    <div className="sh">
                      <div className="si"><i className="fas fa-coins" /></div>
                      <div className="sn">{pkg.loading ? <SkeletonLine $w="8.6rem" $h="0.95rem" /> : pkg.label}</div>
                    </div>
                    <div className="sp">{pkg.loading ? <SkeletonLine $w="5.6rem" $h="1.2rem" /> : pkg.monthlyPrice}</div>
                    <div className="sd">{`${pkg.monthlyCredits} credits per month`}</div>
                    <div className="sd" style={{ color: '#16a34a', fontWeight: 700 }}>3 days for $1</div>
                  </SportCard>
                ))}
              </SportCardGrid>

              {/* What's included */}
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
                  What&apos;s included
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                  {PACKAGE_FEATURES.map((f, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', fontSize: '0.88rem', color: '#475569' }}>
                      <i className={f.icon} style={{ color: '#3b82f6', width: 16, textAlign: 'center', flexShrink: 0 }} />
                      {f.text}
                    </li>
                  ))}
                  {PROPS_FEATURES.map((f, i) => (
                    <li key={`props-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', fontSize: '0.88rem', color: '#64748b', fontStyle: 'italic' }}>
                      <i className={f.icon} style={{ color: '#f59e0b', width: 16, textAlign: 'center', flexShrink: 0 }} />
                      {f.text}
                    </li>
                  ))}
                </ul>

                <div
                  style={{
                    marginTop: '1rem',
                    border: '1px solid rgba(148,163,184,0.35)',
                    borderRadius: 12,
                    padding: '0.85rem 0.9rem',
                    background: '#f8fafc',
                  }}
                >
                  <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a', marginBottom: 3 }}>
                    Optional Props Add-On
                  </div>
                  <div style={{ fontSize: '0.84rem', color: '#475569', marginBottom: 8 }}>
                    {propsAddon?.monthlyPrice ? `${propsAddon.monthlyPrice}/mo` : 'Price shown after Stripe loads'} for props and roster insights access.
                  </div>
                  {currentUser ? (
                    <button
                      type="button"
                      onClick={startPropsAddonCheckout}
                      disabled={submitting || packageLoading || Boolean(packageError)}
                      style={{
                        border: '1px solid rgba(59,130,246,0.4)',
                        background: 'rgba(59,130,246,0.1)',
                        color: '#1d4ed8',
                        borderRadius: 8,
                        padding: '0.45rem 0.75rem',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        cursor: 'pointer'
                      }}
                    >
                      Add Props Access
                    </button>
                  ) : (
                    <div style={{ fontSize: '0.77rem', color: '#64748b', marginTop: 6 }}>
                      New users can include this add-on from the Launch Checkout panel below.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <RightBox>
              <div ref={checkoutFormRef} />
              <TopLine>Step 2</TopLine>
              <SecH2>Launch Checkout</SecH2>
              {CHECKOUT_FEATURES.map(feature => (
                <FeatRow key={feature}><i className="fas fa-check-circle" /><span>{feature}</span></FeatRow>
              ))}

              <CheckoutCard>
                <CheckoutStack as="form" onSubmit={handleSubmit(startCheckout)} noValidate>
                  {currentUser ? (
                    <div style={{
                      borderRadius: 12,
                      border: '1px solid #bfdbfe',
                      background: '#eff6ff',
                      padding: '1rem 1.1rem',
                    }}>
                      <div style={{ fontWeight: 700, color: '#1e40af', fontSize: '0.9rem', marginBottom: 4 }}>
                        <i className="fas fa-user-circle" style={{ marginRight: 6 }} />
                        You&apos;re signed in as {currentUser.email}
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#3b82f6', marginBottom: '0.75rem', lineHeight: 1.5 }}>
                        This page is for new user registration. To change plans or buy top-up credits, go to Subscription Management.
                      </div>
                      <button
                        type="button"
                        onClick={() => history.push('/settings?tab=subscription-management')}
                        style={{
                          background: '#1d4ed8',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          padding: '0.55rem 1.1rem',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          width: '100%',
                        }}
                      >
                        Go to Subscription Management
                      </button>
                    </div>
                  ) : (
                    <>
                      <EmailGroup>
                        <EmailLabel htmlFor="checkoutEmail">Email For Account Provisioning</EmailLabel>
                        <Input
                          id="checkoutEmail"
                          type="email"
                          placeholder="you@example.com"
                          {...register('checkoutEmail', {
                            required: 'Enter your email to continue to checkout.',
                            pattern: {
                              value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
                              message: 'Enter a valid email address.'
                            }
                          })}
                        />
                        {errors.checkoutEmail && <ErrorText>{errors.checkoutEmail.message}</ErrorText>}
                      </EmailGroup>
                      <EmailGroup>
                        <EmailLabel htmlFor="checkoutName">Name / Display Name</EmailLabel>
                        <Input
                          id="checkoutName"
                          type="text"
                          placeholder="Jordan Smith"
                          {...register('checkoutName', {
                            required: 'Enter your name so we can create your account after payment.',
                            minLength: { value: 2, message: 'Name must be at least 2 characters.' },
                            maxLength: { value: 60, message: 'Name must be 60 characters or less.' }
                          })}
                        />
                        {errors.checkoutName && <ErrorText>{errors.checkoutName.message}</ErrorText>}
                      </EmailGroup>
                      <EmailGroup>
                        <EmailLabel htmlFor="checkoutPassword">Password</EmailLabel>
                        <Input
                          id="checkoutPassword"
                          type="password"
                          placeholder="Create a password"
                          {...register('checkoutPassword', {
                            required: 'Enter a password so we can create your account after payment.',
                            minLength: { value: 8, message: 'Password must be at least 8 characters.' },
                            validate: {
                              hasUpper:   value => /[A-Z]/.test(value)       || 'Password must include an uppercase letter.',
                              hasLower:   value => /[a-z]/.test(value)       || 'Password must include a lowercase letter.',
                              hasNumber:  value => /\d/.test(value)          || 'Password must include a number.',
                              hasSpecial: value => /[^A-Za-z0-9]/.test(value)|| 'Password must include a special character.',
                              noSpaces:   value => !/\s/.test(value)         || 'Password cannot contain spaces.'
                            }
                          })}
                        />
                        <PasswordHint>Use at least 8 characters with uppercase, lowercase, number, and special character.</PasswordHint>
                        {errors.checkoutPassword && <ErrorText>{errors.checkoutPassword.message}</ErrorText>}
                      </EmailGroup>
                    </>
                  )}
                  <SelectedPackageWrap>
                    <SelectedPackageLabel>Selected Package</SelectedPackageLabel>
                    <SelectedPackageName>
                      <i className="fas fa-coins" style={{ marginRight: 6 }} />
                      {selected?.loading ? <SkeletonLine $w="10rem" $h="1.1rem" /> : selected?.label}
                    </SelectedPackageName>
                    <SelectedPackageMeta>
                      {selected?.loading
                        ? <SkeletonLine $w="13rem" $h="0.82rem" />
                        : `${selected?.monthlyPrice || ''}/mo • ${selected?.monthlyCredits || 0} credits`}
                    </SelectedPackageMeta>
                    {!selected?.loading && (
                      <SelectedPackageMeta style={{ color: '#16a34a', fontWeight: 700 }}>
                        <i className="fas fa-gift" style={{ marginRight: 6 }} />
                        3 days for $1 - $1 due today, plan price charged on day 4
                      </SelectedPackageMeta>
                    )}
                    {!currentUser && includePropsAddon && (
                      <SelectedPackageMeta>
                        + {propsAddon?.monthlyPrice || 'Props add-on price unavailable'} for Props Add-On
                      </SelectedPackageMeta>
                    )}
                    {!currentUser && (
                      <div style={{ marginTop: '0.6rem' }}>
                        <label
                          htmlFor="include-props-addon"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '0.8rem',
                            color: '#334155',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          <input
                            id="include-props-addon"
                            type="checkbox"
                            checked={includePropsAddon}
                            onChange={e => setIncludePropsAddon(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                          />
                          Add Props Add-On during account creation
                        </label>
                        <div style={{ fontSize: '0.77rem', color: '#64748b', marginTop: 6 }}>
                          This includes the Props add-on in the same Stripe checkout as your selected plan.
                        </div>
                      </div>
                    )}
                  </SelectedPackageWrap>

                  {!currentUser && (
                    <ActionBtn type="submit" disabled={submitting || packageLoading || Boolean(packageError)}>
                      {submitting ? 'Redirecting...' : 'Start Your 3-Day Trial'}
                    </ActionBtn>
                  )}

                  {error && <ErrorText>{error}</ErrorText>}
                </CheckoutStack>
              </CheckoutCard>
            </RightBox>
          </TwoCol>
        </SecInner>
      </Sec>

      <Sec bg="linear-gradient(145deg, #090909 0%, #18120d 50%, #090909 100%)">
        <SecInner>
          <TopLine light>How It Works</TopLine>
          <SecH2 light>Four steps from purchase to predictions.</SecH2>
          <SecDesc light>The prediction system is credit-based: every game unlock costs either 1 or 3 credits based on PW Rating, and you control where those credits are spent.</SecDesc>
          <HowGrid>
            <HowStep>
              <HowNum>1</HowNum>
              <HowIcon><i className="fas fa-layer-group" /></HowIcon>
              <h4>Choose Monthly Credits</h4>
              <p>Select a plan based on monthly credits. Higher tiers give you more prediction unlocks per month.</p>
            </HowStep>
            <HowStep>
              <HowNum>2</HowNum>
              <HowIcon><i className="fas fa-coins" /></HowIcon>
              <h4>Understand Unlock Cost</h4>
              <p>Each game costs 1 credit to unlock, or 3 credits when the PW Rating is 70% or higher.</p>
            </HowStep>
            <HowStep>
              <HowNum>3</HowNum>
              <HowIcon><i className="fas fa-credit-card" /></HowIcon>
              <h4>Checkout And Provisioning</h4>
              <p>Complete Stripe checkout and your account is provisioned automatically. You can include Props add-on at sign-up.</p>
            </HowStep>
            <HowStep>
              <HowNum>4</HowNum>
              <HowIcon><i className="fas fa-table-cells-large" /></HowIcon>
              <h4>Spend Credits Strategically</h4>
              <p>Unlock only the games you want. Credits roll over month to month, and top-ups are available if needed.</p>
            </HowStep>
          </HowGrid>

          <HowFeatures>
            <HowFeat><i className="fas fa-chart-bar" /><div><strong>Multi-Window Game Cards</strong><span>Power, lines, public %, key factors, totals, injuries, rosters, trends, schedule, prediction — all in one card.</span></div></HowFeat>
            <HowFeat><i className="fas fa-brain" /><div><strong>Machine-Learned PW Rating</strong><span>Every pick is rated 0–100 by a custom ML model trained on historical prediction accuracy.</span></div></HowFeat>
            <HowFeat><i className="fas fa-bolt" /><div><strong>Proven Line Pricing</strong><span>Proprietary moneyline and spread pricing on every matchup — not just a raw consensus line.</span></div></HowFeat>
            <HowFeat><i className="fas fa-futbol" /><div><strong>Credit Top-Ups</strong><span>Buy one-time credit packs in Subscription Management when you run low before monthly refill.</span></div></HowFeat>
          </HowFeatures>
        </SecInner>
      </Sec>

      <Sec bg="#f7f9fc">
        <SecInner>
          <TopLine>System Documentation</TopLine>
          <SecH2>Understand the system before you place your plays.</SecH2>
          <SecDesc>Our docs walk through how predictions, PW Rating, and research windows work together so you can use every data point effectively.</SecDesc>
          <SysDocGrid>
            <SysDocCard>
              <div className="ico"><i className="fas fa-lightbulb" /></div>
              <h4>Understanding the System</h4>
              <p>Learn what each game-card signal means — predictions, proven line, PW Rating, and how they layer together into a full matchup picture.</p>
            </SysDocCard>
            <SysDocCard>
              <div className="ico"><i className="fas fa-percent" /></div>
              <h4>PW Rating Scores</h4>
              <p>A 0–100 ML-powered signal based on historical prediction accuracy in similar matchups. Use it to rank value, not just pick winners.</p>
            </SysDocCard>
            <SysDocCard>
              <div className="ico"><i className="fas fa-tools" /></div>
              <h4>Research Windows</h4>
              <p>Ten data windows per card — power, lines, public%, factors, totals, injuries, rosters, trends, schedule, prediction. Build a repeatable workflow.</p>
            </SysDocCard>
          </SysDocGrid>
          <DocsCtaWrap>
            <DocsCtaLink href="/system-docs">
              <i className="fas fa-book-open" />Open System Docs
            </DocsCtaLink>
          </DocsCtaWrap>
        </SecInner>
      </Sec>

      <Sec bg="linear-gradient(145deg, #090909 0%, #16100a 50%, #090909 100%)">
        <SecInner>
          <TopLine light>Follow Us</TopLine>
          <SecH2 light>Stay connected with Proven Winner.</SecH2>
          <SecDesc light>
            Get product updates, card insights, and betting context across X, Instagram, and Winible.
          </SecDesc>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem', marginTop: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 20, padding: '2.5rem 2rem', background: 'rgba(255,255,255,0.06)', textAlign: 'center' }}>
              <i className="fab fa-x-twitter" style={{ fontSize: '3.5rem', color: '#ffffff' }} />
              <h4 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#ffffff', fontSize: '1.15rem' }}>X (Twitter)</h4>
              <p style={{ margin: 0, color: '#cbd5e1', fontSize: '0.9rem', lineHeight: 1.55 }}>Get the latest picks context and model notes from Proven Winner.</p>
              <a href={SOCIAL_LINKS.x} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', background: '#ffffff', color: '#000000', fontWeight: 700, fontSize: '0.9rem', padding: '0.6rem 1.4rem', borderRadius: 999, textDecoration: 'none', boxShadow: '0 4px 14px rgba(255,255,255,.15)', marginTop: '0.25rem' }}>
                <i className="fab fa-x-twitter" /> Follow on X
              </a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 20, padding: '2.5rem 2rem', background: 'rgba(255,255,255,0.06)', textAlign: 'center' }}>
              <i className="fab fa-instagram" style={{ fontSize: '3.5rem', color: '#E4405F' }} />
              <h4 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#E4405F', fontSize: '1.15rem' }}>Instagram</h4>
              <p style={{ margin: 0, color: '#cbd5e1', fontSize: '0.9rem', lineHeight: 1.55 }}>Behind-the-scenes content, updates, and daily card highlights.</p>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', color: '#ffffff', fontWeight: 700, fontSize: '0.9rem', padding: '0.6rem 1.4rem', borderRadius: 999, textDecoration: 'none', boxShadow: '0 4px 14px rgba(228,64,95,.4)', marginTop: '0.25rem' }}>
                <i className="fab fa-instagram" /> Follow on Instagram
              </a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 20, padding: '2.5rem 2rem', background: 'rgba(255,255,255,0.06)', textAlign: 'center' }}>
              <i className="fas fa-chart-line" style={{ fontSize: '3.5rem', color: '#4ade80' }} />
              <h4 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#4ade80', fontSize: '1.15rem' }}>Winible</h4>
              <p style={{ margin: 0, color: '#cbd5e1', fontSize: '0.9rem', lineHeight: 1.55 }}>Track our documented pick record and betting history on Winible.</p>
              <a href={SOCIAL_LINKS.winible} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', background: '#16a34a', color: '#ffffff', fontWeight: 700, fontSize: '0.9rem', padding: '0.6rem 1.4rem', borderRadius: 999, textDecoration: 'none', boxShadow: '0 4px 14px rgba(22,163,74,.4)', marginTop: '0.25rem' }}>
                <i className="fas fa-chart-line" /> Follow on Winible
              </a>
            </div>
          </div>
        </SecInner>
      </Sec>

      <Sec bg="white">
        <PreviewInner>
          <TopLine>Game Card Preview</TopLine>
          <SecH2>Full Multi-Window Card Experience</SecH2>
          <SecDesc>
            Every game card includes power, lines, public data, factors, totals, injuries, rosters, trends, and schedule.
            Prediction internals unlock with credits when you want to go deeper.
          </SecDesc>
          <PreviewGrid>
            <GameCard game={previewGame} sport={PREVIEW_SPORT} defaultWindow="power-rank" demo />
            <GameCard game={previewGame} sport={PREVIEW_SPORT} defaultWindow="prediction" demo />
          </PreviewGrid>
        </PreviewInner>
      </Sec>
    </>
  );
};

export default Packages;
